import React from 'react';
import { Badge } from "@shared/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@shared/ui/accordion";
import { BarChart2, Droplet, Waves, Trash2, Zap, Flame, FileText } from "lucide-react";
import { cn } from "@shared/lib/utils";

const SERVICE_CONFIG: Record<string, { icon: any, theme: string }> = {
  water: { icon: Droplet, theme: "blue" },
  sewer: { icon: Waves, theme: "teal" },
  waste_water: { icon: Waves, theme: "teal" },
  trash: { icon: Trash2, theme: "green" },
  electricity: { icon: Zap, theme: "purple" },
  gas: { icon: Flame, theme: "orange" },
};
const FALLBACK_CONFIG = { icon: FileText, theme: "blue" };

import FlatRate from './rateTypes/FlatRate';
import SlabBased from './rateTypes/SlabBased';
import TimeOfUse from './rateTypes/TimeOfUse';
import Seasonal from './rateTypes/Seasonal';

export function RateStructures({ planConfig }: { planConfig?: any }) {
  const normalizedServices = React.useMemo(() => {
    const res = planConfig?.result || planConfig;
    const ratesObj = res?.rates || res?.utilityServiceData?.rate;
    if (!ratesObj || typeof ratesObj !== 'object') return [];
    
    const items: any[] = [];
    
    Object.entries(ratesObj).forEach(([serviceKey, serviceData]: [string, any]) => {
      const normalizedKey = serviceKey.replace(/\s+/g, '').toLowerCase();

      const getTypeDisplay = (typeCode: string | number) => {
        if (typeCode === undefined || typeCode === null || typeCode === "") return "N/A";
        const code = String(typeCode);
        if (code === '3' || code.toUpperCase() === 'FLAT') return "Flat Rate";
        if (code === '4' || code.toUpperCase() === 'TIERED' || code.toUpperCase() === 'SLAB') return "Slab Based";
        if (code === '5' || code.toUpperCase() === 'TIME OF USE' || code.toUpperCase() === 'TOU') return "Time of Use";
        if (code === '6' || code.toUpperCase() === 'SEASONAL') return "Seasonal";
        if (code === '7' || code.toUpperCase() === 'FIXED') return "Fixed";
        return "N/A";
      };

      const actualData = serviceData?.rate && typeof serviceData.rate === 'object' && !Array.isArray(serviceData.rate)
        ? serviceData.rate
        : serviceData;

      const rawType = actualData?.rateType ?? actualData?.rate_type ?? actualData?.basisType ?? actualData?.basis_type;
      const typeDisplay = getTypeDisplay(rawType);
      const rateVal = actualData?.rate ?? actualData?.slabs ?? actualData?.slots ?? actualData?.structure ?? actualData;

      if (actualData?.conditional) {
        if (actualData.belowRate) {
          items.push({
            id: `${normalizedKey}_below`,
            name: `${serviceKey} (Below Threshold)`,
            typeDisplay: getTypeDisplay(actualData.belowRate.rateType || actualData.belowRate.rate_type || actualData.belowRate.basisType || actualData.belowRate.basis_type),
            basisType: actualData.belowRate.basisType || actualData.belowRate.basis_type || actualData.belowRate.rateType || actualData.belowRate.rate_type,
            rate: actualData.belowRate.rate,
            badgeLabel: `≤ ${actualData.threshold ?? 0} ${actualData.thresholdUnit && actualData.thresholdUnit !== 'null' && actualData.thresholdUnit !== 'N/A' ? actualData.thresholdUnit : 'N/A'}`.trim()
          });
        }
        if (actualData.aboveRate) {
          items.push({
            id: `${normalizedKey}_above`,
            name: `${serviceKey} (Above Threshold)`,
            typeDisplay: getTypeDisplay(actualData.aboveRate.rateType || actualData.aboveRate.rate_type || actualData.aboveRate.basisType || actualData.aboveRate.basis_type),
            basisType: actualData.aboveRate.basisType || actualData.aboveRate.basis_type || actualData.aboveRate.rateType || actualData.aboveRate.rate_type,
            rate: actualData.aboveRate.rate,
            badgeLabel: `> ${actualData.threshold ?? 0} ${actualData.thresholdUnit && actualData.thresholdUnit !== 'null' && actualData.thresholdUnit !== 'N/A' ? actualData.thresholdUnit : 'N/A'}`.trim()
          });
        }
      } else if (actualData) {
        items.push({
          id: normalizedKey,
          name: serviceKey,
          typeDisplay: typeDisplay,
          basisType: actualData?.basisType || actualData?.basis_type || rawType,
          rate: Array.isArray(rateVal) ? rateVal : (Array.isArray(actualData?.rate) ? actualData.rate : [actualData]),
          badgeLabel: "Standard Rate"
        });
      }
    });
    
    return items;
  }, [planConfig]);

  if (normalizedServices.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <BarChart2 className="w-4 h-4 text-gray-400" />
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Rate Structures</h3>
        <div className="h-px bg-gray-200 flex-1 ml-2"></div>
      </div>

      <Accordion type="multiple" defaultValue={normalizedServices.map((s, idx) => `${s.id}-${idx}`)} className="space-y-3">
        {normalizedServices.map((service, index) => {
          const configId = service.id?.toLowerCase().split('_')[0];
          const config = (configId ? SERVICE_CONFIG[configId] : undefined) || SERVICE_CONFIG[service.name?.toLowerCase()] || FALLBACK_CONFIG;
          const Icon = service.icon || config.icon;
          const currentTheme = service.theme || config.theme;
          
          const themeClasses: Record<string, any> = {
            blue: {
              item: "border-blue-100 [&[data-state=open]]:border-blue-200",
              trigger: "bg-blue-50/60 hover:bg-blue-50/90 data-[state=open]:bg-blue-50 [&>svg]:text-blue-400",
              icon: "text-blue-500",
              title: "text-gray-900",
              subtitle: "text-blue-600/80",
              badge: "bg-blue-100/50 text-blue-700 hover:bg-blue-100/50",
              content: "border-blue-100"
            },
            teal: {
              item: "border-teal-100 [&[data-state=open]]:border-teal-200",
              trigger: "bg-teal-50/60 hover:bg-teal-50/90 data-[state=open]:bg-teal-50 [&>svg]:text-teal-400",
              icon: "text-teal-600",
              title: "text-gray-900",
              subtitle: "text-teal-700/80",
              badge: "bg-teal-100/60 text-teal-700 hover:bg-teal-100/60",
              content: "border-teal-100"
            },
            green: {
              item: "border-green-100 [&[data-state=open]]:border-green-200",
              trigger: "bg-green-50/60 hover:bg-green-50/90 data-[state=open]:bg-green-50 [&>svg]:text-green-400",
              icon: "text-green-600",
              title: "text-gray-900",
              subtitle: "text-green-700/80",
              badge: "bg-green-100/60 text-green-700 hover:bg-green-100/60",
              content: "border-green-100"
            },
            purple: {
              item: "border-purple-100 [&[data-state=open]]:border-purple-200",
              trigger: "bg-purple-50/60 hover:bg-purple-50/90 data-[state=open]:bg-purple-50 [&>svg]:text-purple-400",
              icon: "text-purple-600",
              title: "text-gray-900",
              subtitle: "text-purple-700/80",
              badge: "bg-purple-100/60 text-purple-700 hover:bg-purple-100/60",
              content: "border-purple-100"
            },
            orange: {
              item: "border-orange-100 [&[data-state=open]]:border-orange-200",
              trigger: "bg-orange-50/60 hover:bg-orange-50/90 data-[state=open]:bg-orange-50 [&>svg]:text-orange-400",
              icon: "text-orange-600",
              title: "text-gray-900",
              subtitle: "text-orange-700/80",
              badge: "bg-orange-100/60 text-orange-700 hover:bg-orange-100/60",
              content: "border-orange-100"
            }
          };

          const tc = themeClasses[currentTheme] || themeClasses.blue;

          let content = null;
          if (service.typeDisplay === "Time of Use" || service.basisType == "Time Of Use" ) {
            content = <TimeOfUse slots={(service.rate || []) as any[]} />;
          } else if (service.typeDisplay === "Slab Based" || service.basisType == "Slab" || service.basisType == "Tiered") {
            content = <SlabBased slabs={(service.rate || []) as any[]} />;
          } else if (service.typeDisplay === "Seasonal" || service.basisType == "Seasonal") {
            content = <Seasonal seasons={(service.rate || []) as any[]} />;
          } else {
            content = <FlatRate data={service.rate || []} />;
          }

          const displayName = service.name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

          return (
            <AccordionItem key={`${service.id}-${index}`} value={`${service.id}-${index}`} className={cn("rounded-xl overflow-hidden bg-white shadow-sm transition-colors border", tc.item)}>
              <AccordionTrigger className={cn("hover:no-underline px-4 py-3 transition-colors", tc.trigger)}>
                <div className="flex items-center justify-between w-full pr-3">
                  <div className="flex items-center gap-3">
                    <Icon className={cn("w-4 h-4 shrink-0", tc.icon)} />
                    <div className="text-left">
                      <p className={cn("font-semibold text-sm", tc.title)}>{displayName}</p>
                      <p className={cn("text-xs font-medium", tc.subtitle)}>{service.typeDisplay}</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className={cn("border-0 font-medium text-xs px-2 py-0.5 rounded-md", tc.badge)}>
                    {service.badgeLabel}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className={cn("px-4 py-4 border-t", tc.content)}>
                {content}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

export default RateStructures;
